import { useEffect, useState } from "react";
import { Book, Check, CloudOff, RotateCw, Send } from "lucide-react";
import { ActionButton } from "./kit";
import { FailureCard, SavedCard } from "./GoalNotice";
import api from "../lib/api";
import { assignGuide, fetchAssignedGuides } from "../lib/guides";
import { joinNames } from "../lib/drillHolders";

/**
 * "Assign this guide" under a guide's body, for a coach on the sections look
 * (#1159 slice 3, board CoachGuideAssign, D251, D298). The redraw of
 * `GuidePage.jsx`'s `GuideAssignPanel` with kit tokens and no shadcn `Card`,
 * `Badge`, `Button` or `Checkbox`. The shipped panel stays in `GuidePage.jsx`,
 * byte for byte, for admin and for the old look (D217); only a coach on the
 * sections look mounts this one.
 *
 * The logic is the shipped panel's, unchanged: the roster from `GET /players`
 * (a coach gets their active links), who holds the guide from
 * `fetchAssignedGuides` per player (best-effort: a failed lookup leaves that
 * player unticked), N independent `assignGuide` posts (`Promise.allSettled`,
 * #481 D1/D2), a load-time tick posts nothing, and an assigned player ticked
 * again by the coach is a deliberate re-send (D3).
 *
 * What the board changes: "Has it" for "Assigned"; the one-holder line;
 * success is a line that stays (D48), not a toast; loading is three shapes.
 * Two or more holders are not drawn (S7), so the line shows for exactly one.
 */
const CARD = { background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)" };
const NAME = { fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 15, color: "var(--nw-ink)", minWidth: 0, overflowWrap: "anywhere" };
const CHIP = {
  display: "inline-flex", alignItems: "center", padding: "2px 8px", background: "var(--nw-line-faint)", color: "var(--nw-ink-lede)",
  fontSize: 11, fontWeight: 600, borderRadius: "var(--nw-radius-pill)", whiteSpace: "nowrap",
};

function Loading() {
  return (
    <div data-testid="guide-assign-loading" aria-busy="true" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {[0, 1, 2].map((i) => (
        <div key={i} style={{ ...CARD, display: "flex", flexDirection: "column", gap: 8, padding: "12px 14px", height: 52, boxSizing: "border-box" }}>
          <div style={{ width: "50%", height: 12, borderRadius: 6, background: "var(--nw-line)" }} />
          <div style={{ width: "30%", height: 10, borderRadius: 5, background: "var(--nw-line-faint)" }} />
        </div>
      ))}
    </div>
  );
}

function Failed({ onRetry }) {
  return (
    <div data-testid="guide-assign-load-failed" style={{ ...CARD, display: "flex", flexDirection: "column", gap: 8, padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <CloudOff size={20} aria-hidden="true" style={{ color: "var(--nw-alert)", flexShrink: 0 }} />
        <div className="nw-body-strong">Couldn't load your players</div>
      </div>
      <div style={{ fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink-muted)" }}>Nothing has been lost — we just couldn't reach the server.</div>
      <button
        type="button"
        data-testid="guide-assign-load-failed-retry"
        onClick={onRetry}
        style={{
          display: "inline-flex", alignItems: "center", gap: 6, alignSelf: "flex-start", minHeight: 44, padding: "0 14px", background: "var(--nw-surface)",
          border: "1px solid var(--nw-line-strong)", borderRadius: "var(--nw-radius-control)", fontSize: 14, fontWeight: 600, color: "var(--nw-navy)", cursor: "pointer",
        }}
      >
        <RotateCw size={14} aria-hidden="true" />
        Try again
      </button>
    </div>
  );
}

function Tick({ checked, name, onToggle, testId }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={`Assign to ${name}`}
      data-testid={testId}
      onClick={onToggle}
      style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", width: 44, height: 44, padding: 0, background: "none", border: 0, cursor: "pointer" }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 22, height: 22, boxSizing: "border-box", borderRadius: "var(--nw-radius-check)", display: "inline-flex", alignItems: "center", justifyContent: "center",
          ...(checked ? { background: "var(--nw-navy)" } : { background: "var(--nw-surface)", border: "2px solid var(--nw-line-strong)" }),
        }}
      >
        {checked && <Check size={14} strokeWidth={3} style={{ color: "#fff" }} />}
      </span>
    </button>
  );
}

function Heading({ lede }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
      <div
        aria-hidden="true"
        style={{ width: 36, height: 36, flexShrink: 0, borderRadius: 10, background: "var(--nw-navy-tint)", display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Book size={16} style={{ color: "var(--nw-navy)" }} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
        <h2 style={{ margin: 0, fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 16, color: "var(--nw-ink)" }}>Assign this guide</h2>
        <div style={{ fontSize: 13, lineHeight: 1.45, color: "var(--nw-ink-muted)" }} data-testid="guide-assign-lede">{lede}</div>
      </div>
    </div>
  );
}

export default function CoachGuideAssignPanel({ category, slug }) {
  const [roster, setRoster] = useState(null); // null = still loading
  const [rosterFailed, setRosterFailed] = useState(false);
  const [assignedIds, setAssignedIds] = useState(() => new Set());
  const [tickedIds, setTickedIds] = useState(() => new Set());
  const [retickedIds, setRetickedIds] = useState(() => new Set());
  const [assigning, setAssigning] = useState(false);
  const [failedNames, setFailedNames] = useState([]);
  const [doneLine, setDoneLine] = useState("");
  const [loadKey, setLoadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setRosterFailed(false);
    setRoster(null);
    api
      .get("/players")
      .then(async (r) => {
        const players = r.data;
        const settled = await Promise.allSettled(players.map((p) => fetchAssignedGuides(p.player_id)));
        if (cancelled) return;
        const assigned = new Set();
        settled.forEach((res, i) => {
          if (res.status === "fulfilled" && res.value.some((a) => a.category === category && a.slug === slug)) {
            assigned.add(players[i].player_id);
          }
        });
        setRoster(players);
        setAssignedIds(assigned);
        setTickedIds(new Set(assigned));
        setRetickedIds(new Set());
      })
      .catch(() => {
        if (!cancelled) setRosterFailed(true);
      });
    return () => { cancelled = true; };
  }, [category, slug, loadKey]);

  const toggle = (playerId) => {
    const turningOn = !tickedIds.has(playerId);
    const next = new Set(tickedIds);
    if (turningOn) next.add(playerId);
    else next.delete(playerId);
    setTickedIds(next);
    setDoneLine("");
    // F5: unticking a player who failed clears their failure; the card must not name someone no longer ticked.
    if (!turningOn) {
      const name = (roster || []).find((p) => p.player_id === playerId)?.full_name;
      setFailedNames((cur) => cur.filter((n) => n !== name));
    }
    // D3: an assigned player ticked BY THE COACH (not by the load) is a deliberate re-assign.
    if (turningOn && assignedIds.has(playerId)) setRetickedIds(new Set(retickedIds).add(playerId));
  };

  const targets = (roster || []).filter(
    (p) => tickedIds.has(p.player_id) && (!assignedIds.has(p.player_id) || retickedIds.has(p.player_id)),
  );

  const assignTargets = async () => {
    if (assigning || targets.length === 0) return;
    setAssigning(true);
    setFailedNames([]);
    setDoneLine("");
    // D1/D2: N independent posts, successes stand, failures named.
    const results = await Promise.allSettled(targets.map((p) => assignGuide(p.player_id, category, slug)));
    const ok = targets.filter((_, i) => results[i].status === "fulfilled");
    const failed = targets.filter((_, i) => results[i].status !== "fulfilled");
    if (ok.length > 0) {
      const nextAssigned = new Set(assignedIds);
      const nextReticked = new Set(retickedIds);
      ok.forEach((p) => { nextAssigned.add(p.player_id); nextReticked.delete(p.player_id); });
      setAssignedIds(nextAssigned);
      setRetickedIds(nextReticked);
      // D48: a success is a line that stays, not a toast.
      // D259: the board draws only the failure card on a partial failure, so the success line is for a clean run.
      if (failed.length === 0) setDoneLine(ok.length === 1 ? `Assigned to ${ok[0].full_name}.` : `Assigned to ${ok.length} players.`);
    }
    setFailedNames(failed.map((p) => p.full_name));
    setAssigning(false);
  };

  if (rosterFailed) return <Failed onRetry={() => setLoadKey((k) => k + 1)} />;
  if (roster === null) return <Loading />;

  const panel = { ...CARD, display: "flex", flexDirection: "column", gap: 12, padding: 14 };

  if (roster.length === 0) {
    return (
      <div data-testid="guide-assign-panel" style={panel}>
        <Heading lede={<span data-testid="guide-assign-empty">No linked players to assign this guide to yet.</span>} />
      </div>
    );
  }

  const holders = roster.filter((p) => assignedIds.has(p.player_id));
  const label = assigning
    ? "Assigning…"
    : targets.length === 1
      ? `Assign to ${targets[0].full_name}`
      : targets.length > 1
        ? `Assign to ${targets.length} players`
        : "Assign";

  return (
    <div data-testid="guide-assign-panel" style={panel}>
      <Heading lede="Your players see it on Training & guides, with your name on it." />

      <div data-testid="guide-assign-roster" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {roster.map((p) => {
          const on = tickedIds.has(p.player_id);
          return (
            <div
              key={p.player_id}
              style={{
                display: "flex", alignItems: "center", gap: 4, minHeight: 52, boxSizing: "border-box", padding: "4px 4px 4px 14px",
                background: "var(--nw-surface)", borderRadius: "var(--nw-radius-card)", border: `1px solid ${on ? "var(--nw-navy)" : "var(--nw-line)"}`,
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 8, flexGrow: 1, minWidth: 0 }}>
                <span style={NAME}>{p.full_name}</span>
                {assignedIds.has(p.player_id) && (
                  <span style={CHIP} data-testid={`guide-assigned-badge-${p.player_id}`}>Has it</span>
                )}
              </span>
              <Tick checked={on} name={p.full_name} testId={`guide-assign-player-${p.player_id}`} onToggle={() => toggle(p.player_id)} />
            </div>
          );
        })}
      </div>

      {/* The board draws one holder; two or more are not drawn (S7), so no line for them. */}
      {holders.length === 1 && (
        <div className="nw-help" data-testid="guide-assign-reassign-hint">
          {`${holders[0].full_name} already has this guide. Ticking them again sends it, and its notification, again.`}
        </div>
      )}

      {failedNames.length > 0 && (
        <FailureCard
          data-testid="guide-assign-failures"
          title={`Couldn't assign to ${joinNames(failedNames)}.`}
          body="They're still ticked. Press Assign to try again."
        />
      )}

      {doneLine && <SavedCard data-testid="guide-assign-done">{doneLine}</SavedCard>}

      <ActionButton
        onClick={assignTargets}
        disabled={targets.length === 0 || assigning}
        icon={<Send size={18} aria-hidden="true" color={assigning ? "#475569" : "#0F172A"} />}
        // D344: the kit gold, as the board draws it (D259). The body's reps chip yields on the coach's page.
        // The board draws Assigning… grey.
        style={assigning ? { background: "var(--nw-line)", color: "var(--nw-ink-lede)", opacity: 1 } : undefined}
        data-testid="guide-assign-btn"
      >
        {label}
      </ActionButton>
    </div>
  );
}
